import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useQueries, useQuery } from 'react-query';

// types
import {
  OKVItemType,
  type CategoryTreeRoot,
  type OKVCategory,
  type OKVItem,
  type OKVJsonContent,
  type OKVTile,
} from './online-catalog-assets.types';

// utils
import { fetchProducts } from 'components/Product/actions';
import { useIsPreview } from 'utils/hooks/useIsPreview';
import { catalogBrandNameSelector } from 'utils/selectors/globalsSelectors';
import getEndpoint from 'utils/endpoints';
import { itemToTile } from 'services/server-data-loaders/online-catalog-assets/category-assets-utils';

const uniq = <T>(items: T[]): T[] => Array.from(new Set(items));

// we need to fetch the whole category tree for each market to find the parent of the category, series or product
// for future implementations ask the mbp team if they can adjust the api calls (pass parent category in one call)
const useFetchCategoryTree = (catalogLocales: string[], brand) => {
  const categoriesQueries = useMemo(
    () =>
      catalogLocales.map((locale) => ({
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        queryKey: ['categoryTree', locale],
        queryFn: async () => {
          const url = `${getEndpoint('categories', {})}/v1/${locale}/categories${brand ? `?brand=${brand}` : ''}`;
          const res = await fetch(url);
          return (await res.json()) as CategoryTreeRoot;
        },
      })),
    [catalogLocales, brand],
  );
  const categoryTree = useQueries(categoriesQueries);

  // to fix useQueries rerender bug:
  // https://github.com/TanStack/query/issues/3049
  const indicatorTimestamp = categoryTree.reduce((acc, cur) => acc + cur.dataUpdatedAt, 0);
  const categoryTreeCached = useMemo(
    () => categoryTree,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [indicatorTimestamp],
  );
  return categoryTreeCached;
};

const getProductCount = async (cat: OKVItem, brand: string | null): Promise<number | undefined> => {
  const url = `${getEndpoint('catalog', {})}/products?lang=${cat.catalogLocale}${
    brand ? `&brand=${brand}` : ''
  }&responseSize=list&limit=1&filter.query=categories.id:subtree(%22${cat.identifier}%22)`;
  const response = await fetch(url);
  const data = await response.json();
  return data?.total;
};

export const useFetchAssets = ({
  data,
  enabled,
  hideCount = false,
}: {
  data: OKVJsonContent[];
  enabled: boolean;
  hideCount?: boolean;
}): { data: OKVJsonContent<OKVItem | OKVTile>[] } => {
  const dispatch = useDispatch();
  const isPreview = useIsPreview();
  const brandName = useSelector(catalogBrandNameSelector);

  const categoryItems = useMemo(() => {
    return uniq(
      data
        .flatMap(({ items }) => items)
        .filter((item) => item.type === OKVItemType.Category || item.type === OKVItemType.Series),
    );
  }, [data]);

  const locales = useMemo(() => {
    return uniq(categoryItems.map((item) => item.catalogLocale));
  }, [categoryItems]);

  const categories = useFetchCategoryTree(locales, brandName);
  const categoriesByLanguage = useMemo(() => {
    return locales.reduce(
      (acc, locale, index) => {
        acc[locale] = categories[index]?.data;
        return acc;
      },
      {} as Record<string, CategoryTreeRoot | undefined>,
    );
  }, [categories, locales]);

  const { data: categoryStore } = useQuery(
    [categoryItems, hideCount],
    async () => {
      const resp = await Promise.all<{ okvCategory: OKVCategory }>(
        categoryItems.map((cat) =>
          (async () => {
            const response = await fetch(
              `${getEndpoint('categories', {})}/v1/${cat.catalogLocale}/categories?rootId=${
              cat.identifier
              }&depth=0&contentFilter=products&brand=${brandName}`,
            );
            const okvCategory = await response.json() as OKVCategory;
            let total: number | undefined = undefined;
            if (!hideCount) {
              total = await getProductCount(cat, brandName);
            }
            return {
              okvCategory: { ...okvCategory, total },
            };
          })(),
        ),
      );

      return resp.reduce(
        (acc, { okvCategory }) => {
          if (okvCategory?.id) {
            acc[okvCategory.id] = okvCategory;
          }
          return acc;
        },
        {} as Record<string, OKVCategory>,
      );
    },
    { refetchOnWindowFocus: false, refetchOnMount: false, enabled },
  );

  const productItems = useMemo(() => {
    return uniq(
      data.flatMap(({ items }) => items).filter((item) => item.type === OKVItemType.Product),
    );
  }, [data]);

  useEffect(() => {
    if (!enabled) return;
    // filter unqiue product items by catalog and call the fetch action
    const productsByCatalog: Record<string, Set<string>> = productItems.reduce((catalog, item) => {
      if (!catalog[item.catalogLocale]) {
        catalog[item.catalogLocale] = new Set();
      }
      catalog[item.catalogLocale].add(item.identifier);
      return catalog;
    }, {});

    Object.entries(productsByCatalog).forEach(([catalog, products]) => {
      dispatch(fetchProducts(Array.from(products), '', catalog) as any);
    });
  }, [dispatch, productItems, enabled]);

  const productCatalogs = useSelector((state: AppState) => state.productCatalogs);

  if (!enabled) return { data: [] };

  return {
    data: data.map((parent) => {
      return {
        ...parent,
        items: parent.items
          .map((item) => {
            if (item.type !== OKVItemType.Product) {
              return itemToTile(item, categoriesByLanguage, categoryStore);
            } else if (productCatalogs?.[item.catalogLocale]?.products?.[item.identifier]) {
              const product = productCatalogs[item.catalogLocale]?.products[item.identifier];
              return {
                product: {
                  ...product,
                  name: product?.categories?.[0]?.name,
                  subtitle: product?.name,
                },
                linkType: 'product_detail_page',
                catalogLanguage: item.catalogLocale,
                type: item.type,
                identifier: item.identifier,
              } as unknown as OKVItem;
            } else if (Object.keys(productCatalogs?.[item.catalogLocale]?.products || {}).length) {
              return { ...item, product: { deprecated: true } };
            }

            return item;
          })
          .flat()
          .filter((item: OKVTile | OKVItem) => isPreview || !item.product?.deprecated) as (
          | OKVTile
          | OKVItem
        )[],
      };
    }),
  };
};
